import { Point } from "pixi.js";
import ConsoleUtil from "../../../../vendor_core/core/src/utils/ConsoleUtil";
import Layout from "../../../../vendor_core/core/src/utils/Layout";
import App from "../../../../vendor_core/core/src/App";
import GameEvents from "../../../../vendor_core/core/src/const/GameEvents";
import PageSettings from "../../../../vendor_core/core/src/ui/settings/PageSettings";
import { SettingsButtons } from "../../../../vendor_core/core/src/const/SettingsButtons";
import TextField from "../../../../vendor_core/core/src/views/components/TextField";
import Configs from "../../../../vendor_core/src/vendorConfigs/Configs";
import BURSettingsPanel from "./BURSettingsPanel";
import { ISettingsPage, parsePage } from "./BURSettingsPages";

/** The checkbox labels of the autoplay page */
const AUTOPLAY_LABEL_FIELD = /^stop_on_/;

/**
 * The settings menu. The vendor PageSettings builds every page of the menu from a PSD scene, which is slow (all of
 * its texts are fitted to their bounds, twice in portrait) and has to be hidden behind the loading screen. Here the
 * paytable and the rules pages come from BURSettingsPanel, which lays out the same texts and images itself. The menu
 * around them and the bet, autoplay and options pages are still the ones of the vendor.
 */
export default class BURSettings extends PageSettings {
    private panel?: BURSettingsPanel;

    constructor() {
        super();
        App.dispatcher.on(GameEvents.RESIZE, this.onResize, this);
    }

    /**
     * The pages are made in the coordinates of the design, so a new size of the screen only moves and scales the
     * panel, which is done right away (the vendor has already placed the menu, as it listens to the event first), so
     * it follows the menu while the window is resized. The pages are made again only when the scale of their sizes
     * or the orientation has changed.
     */
    private onResize(): void {
        const panel = this.panel;
        if (this.isHidden || !panel || !panel.parent) {
            return;
        }

        this.placePanel(panel);
        if (panel.isStale() && this.currentSettingsPage === Configs.settings.rulesScenes.paytable) {
            this.showPage();
        }
    }

    /**
     * Builds all the pages once the resources are loaded, so opening the menu in the game does not wait for anything.
     * It takes a fraction of a second (the vendor pre-build takes seconds), so it is done in one go.
     */
    public preInitialize(initial: boolean) {
        if (!initial) {
            return;
        }

        // a page can't be measured while the game has no size (e.g. it was opened in a background tab). It is built
        // when the menu is opened instead.
        if (Layout.scalePixels(100) > 0) {
            try {
                const panel = this.getPanel();
                for (let index = 0; index < panel.count; index++) {
                    panel.build(index);
                }
            } catch (error) {
                ConsoleUtil.warn("[SETTINGS] The pages could not be built in advance", error);
            }
        }
    }

    /**
     * The vendor creates the menu scene hidden when the assets load, and replaces it with a visible one when the
     * pages are built in advance, which this class does not do. So it is shown here.
     */
    protected setupNavigationInterface() {
        super.setupNavigationInterface();
        this.interfaceScene.visible = true;
    }

    /**
     * The vendor one finds the font size that fits the longest rules text by filling a whole rules page, which blocks
     * the main thread for seconds. It is only used by those texts, which the panel does not use.
     */
    protected calculateMinFontSize(): void {
        if (!this.minFontSize) {
            const fontSize = App.config.minFontSize || 10;
            this.minFontSizeLandscape = fontSize;
            this.minFontSizePortrait = fontSize;
        }
    }

    // --- paytable and rules

    protected showPayTable() {
        this.update();
        const panel = this.getPanel();
        this.currentSettingsPage = Configs.settings.rulesScenes.paytable;
        this.payTablePage = 1;
        this.paytablePageScenes = this.pageModels.map((page) => page.id);
        this.attachPanel(panel);
        this.showPage();
    }

    /** Paging wraps around: "previous" on the first page opens the last one, "next" on the last page the first one */
    protected switchPayTables(direction: string, pagesCount: number) {
        if (direction === "previous") {
            this.payTablePage = this.payTablePage <= 1 ? pagesCount : this.payTablePage - 1;
        } else if (direction === "next") {
            this.payTablePage = this.payTablePage >= pagesCount ? 1 : this.payTablePage + 1;
        }
        this.showPage();
    }

    private showPage(): void {
        this.panel!.show(this.payTablePage - 1);
        if (this.leftArrow) {
            this.leftArrow.visible = true;
        }
        if (this.rightArrow) {
            this.rightArrow.visible = true;
        }
    }

    private pageModels: ISettingsPage[] = [];

    private getPanel(): BURSettingsPanel {
        if (!this.panel) {
            this.pageModels = this.parsePages();
            this.panel = new BURSettingsPanel(this.pageModels);
        }
        return this.panel;
    }

    /** The pages of the paytable and the rules, in the order of the settings config */
    private parsePages(): ISettingsPage[] {
        const scenes = Configs.settings.rulesScenes;
        const layer = this.contentLayer as any;
        const pages: ISettingsPage[] = [];

        Object.values(scenes).forEach((name: string) => {
            const count = this.contentLayer.getScenesTypeCount(name);
            for (let index = 1; index <= count; index++) {
                const pageName = `${name}~${index}`;
                if (!this.hasPageRestriction(pageName)) {
                    const sceneName = `${this.contentLayer.name}-${pageName}`;
                    pages.push(parsePage(sceneName, layer.getSceneConfig(sceneName)));
                }
            }
        });
        return pages;
    }

    /** Puts the panel in the content layer, where the design of the menu is (the same as the header and the buttons) */
    private attachPanel(panel: BURSettingsPanel): void {
        // hides the page of the vendor that is open (it is the content layer's active scene, which setScene would clear)
        this.contentLayer.clear();
        if (panel.parent !== this.contentLayer) {
            this.contentLayer.addChild(panel);
        }
        this.placePanel(panel);
    }

    private placePanel(panel: BURSettingsPanel): void {
        const main = this.interfaceScene?.groups.get("main");
        if (!main) {
            return;
        }

        const position = this.contentLayer.toLocal(main.getGlobalPosition());
        panel.position.copyFrom(position);
        panel.scale.set(main.scale.x, main.scale.y);

        const button = this.interfaceScene.buttons.get(SettingsButtons.SETTINGS);
        if (button) {
            const bounds = button.getBounds();
            const topLeft = panel.toLocal(new Point(bounds.x, bounds.y));
            const bottomRight = panel.toLocal(new Point(bounds.x + bounds.width, bounds.y + bounds.height));
            panel.setHighlight(topLeft.x, topLeft.y, bottomRight.x - topLeft.x, bottomRight.y - topLeft.y);
        }
    }

    private detachPanel(): void {
        this.panel?.parent?.removeChild(this.panel);
    }

    // --- the pages of the vendor

    public showAutoPlay() {
        this.detachPanel();
        super.showAutoPlay();
        this.unifyAutoplayLabels();
    }

    protected showBetOptions() {
        this.detachPanel();
        super.showBetOptions();
    }

    protected showOptions(menuType: SettingsButtons) {
        this.detachPanel();
        super.showOptions(menuType);
    }

    public hide() {
        this.detachPanel();
        super.hide();
    }

    /**
     * The autoplay page is built by the vendor, which fits every text to its own bound, so "Stop on Bonus feature"
     * (limited by its width) comes out smaller than "Stop on Big Win". The two labels share the smaller size.
     */
    private unifyAutoplayLabels(): void {
        const fields: TextField[] = [];
        this.contentLayer.getCurrentScene()?.sceneTextFields?.forEach((field, name) => {
            if (AUTOPLAY_LABEL_FIELD.test(name)) {
                fields.push(field as TextField);
            }
        });
        if (fields.length < 2) {
            return;
        }

        const size = Math.min(...fields.map((field) => Number(field.style.fontSize)));
        fields.forEach((field) => {
            field.fixFontSize = size;
            field.applyBound(1, false);
        });
    }
}
